import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TableSkeleton } from '../Skeleton';
import { UserPickerRow } from '../UserPickerRow';
import { ConfirmDialog } from '../ConfirmDialog';
import { User } from '../../types';
import { useAffiliateUserSearch } from '../../hooks/useAffiliateUserSearch';
import { adminService, CalendarEditorRow, CalendarEditorsAdminState } from '../../services/adminService';
import { useToast } from '../../contexts/ToastContext';
import { Info, Plus, Search, UserMinus, X } from 'lucide-react';
import { ADMIN_LIST_CAPPED, ADMIN_LIST_FILL, ADMIN_TAB_FILL } from './adminLayout';

/**
 * 회사 일정 권한 (관리자 콘솔 '회사 일정 권한' 탭, super_admin 전용)
 *
 * 담당자는 자기 계열사의 회사 일정을 등록·수정·삭제한다. '전체 계열사 공통' 일정은 Super Admin만 쓸 수 있고,
 * Super Admin은 명단 없이 모든 회사 일정을 관리하므로 '자동' 행으로 보인다(해제 버튼 없음).
 * 판정은 서버 calendarAccess가 하고 이 화면은 명단(calendar_company_editors)만 고친다.
 * 지정·해제는 당사자의 캘린더를 서버가 다시 받게 해서 재로그인 없이 편집기의 '회사 일정' 선택지가 바뀐다.
 *
 * 구조는 원격지원 담당자 명단(RemoteSupportManager)과 같고, 사번은 직접 입력하지 않고 명부에서 고른다
 * (LoginApprovalManager와 같은 이유 — 오타가 그대로 저장된다).
 */

interface CalendarEditorManagerProps {
  currentUser: User;
}

export const CalendarEditorManager: React.FC<CalendarEditorManagerProps> = ({ currentUser }) => {
  const { showToast } = useToast();

  const [state, setState] = useState<CalendarEditorsAdminState | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<CalendarEditorRow | null>(null);

  const search = useAffiliateUserSearch({
    isActive: true,
    defaultAffiliateId: currentUser.affiliateId,
  });
  const affiliateLabelOf = useCallback(
    (id: string | null) => (id ? search.affiliates.find(a => a.id === id)?.label ?? id : ''),
    [search.affiliates]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setState(await adminService.getCalendarEditors());
    } catch (err) {
      console.error('Failed to load calendar editors:', err);
      setLoadError(err instanceof Error ? err.message : '회사 일정 담당자를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const editorKeys = useMemo(
    () => new Set((state?.editors ?? []).map(e => `${e.affiliateId}:${e.userId}`)),
    [state]
  );
  // Super Admin(자동). 명단에도 있는 사람은 명단 행에 배지로만 보인다(두 줄로 겹치지 않게)
  const superAdminIds = useMemo(() => new Set((state?.superAdmins ?? []).map(sa => sa.userId)), [state]);
  const superAdminsOnly = useMemo(
    () => (state?.superAdmins ?? []).filter(sa => !(state?.editors ?? []).some(e => e.userId === sa.userId)),
    [state]
  );
  const isEditor = useCallback(
    (userId: string | number) => editorKeys.has(`${search.selectedAffiliateId}:${String(userId)}`),
    [editorKeys, search.selectedAffiliateId]
  );
  const selectedNew = useMemo(
    () => search.selectedUsers.filter(u => !isEditor(u.id)),
    [search.selectedUsers, isEditor]
  );

  const handleGrant = async () => {
    if (!search.selectedAffiliateId || selectedNew.length === 0 || busy) return;
    setBusy(true);
    try {
      const res = await adminService.grantCalendarEditors({
        affiliateId: search.selectedAffiliateId,
        users: selectedNew.map(u => ({ userId: String(u.id), name: u.username, depart: u.depart })),
      });
      search.reset();
      setPickerOpen(false);
      await load();
      showToast({ type: 'success', message: `${res.added}명을 회사 일정 담당자로 지정했습니다.` });
    } catch (err: any) {
      showToast({ type: 'error', message: err?.message || '담당자로 지정하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    } finally {
      setBusy(false);
    }
  };

  const performRevoke = async (row: CalendarEditorRow) => {
    setBusy(true);
    try {
      await adminService.revokeCalendarEditor(row.affiliateId, row.userId);
      await load();
      showToast({ type: 'success', message: `${row.userName || row.userId}님을 회사 일정 담당자에서 해제했습니다.` });
    } catch (err: any) {
      showToast({ type: 'error', message: err?.message || '담당자에서 해제하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    } finally {
      setBusy(false);
    }
  };

  const total = (state?.editors.length ?? 0) + superAdminsOnly.length;

  return (
    <div className={`${ADMIN_TAB_FILL} gap-4`}>
      <div className="flex items-start gap-2 px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800 shrink-0">
        <Info size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
        <span>
          담당자는 <b>자기 계열사</b>의 회사 일정을 등록·수정·삭제할 수 있습니다. '전체 계열사 공통' 일정은 Super Admin만
          만들 수 있습니다. 회사 일정은 직원에게 알림이 가지 않습니다. 해제해도 그 사람이 등록한 일정은 그대로 남습니다.
        </span>
      </div>

      {loadError && (
        <div className="px-3 py-2 bg-brand-50 text-brand-700 text-sm rounded border border-brand-200 flex items-center justify-between shrink-0">
          <span>{loadError}</span>
          <button onClick={load} className="text-brand-700 underline text-xs ml-2 shrink-0">
            다시 시도
          </button>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 shrink-0">
        <div>
          <h3 className="text-sm font-medium text-slate-800">회사 일정 담당자</h3>
          <p className="text-xs text-slate-500">
            Super Admin은 모두 자동으로 담당자입니다. 그 밖에 회사 일정을 올릴 사람을 추가하세요.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setPickerOpen(v => !v)}
          aria-expanded={pickerOpen}
          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
        >
          {pickerOpen ? <X size={14} aria-hidden="true" /> : <Plus size={14} aria-hidden="true" />}
          {pickerOpen ? '닫기' : '담당자 추가'}
        </button>
      </div>

      {pickerOpen && (
        <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl shrink-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {search.showAffiliateSelector && (
              <select
                value={search.selectedAffiliateId}
                onChange={e => search.setSelectedAffiliateId(e.target.value)}
                aria-label="계열사 선택"
                className="px-3 py-1.5 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
              >
                {search.affiliates.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.label}
                  </option>
                ))}
              </select>
            )}
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                aria-hidden="true"
              />
              <input
                type="search"
                value={search.searchQuery}
                onChange={e => search.setSearchQuery(e.target.value)}
                aria-label="직원 검색"
                placeholder="이름이나 부서로 찾기"
                className="w-full pl-8 pr-3 py-1.5 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
            </div>
            <button
              type="button"
              onClick={handleGrant}
              disabled={busy || selectedNew.length === 0}
              className="ml-auto px-3 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
            >
              지정 {selectedNew.length > 0 && `(${selectedNew.length})`}
            </button>
          </div>
          {search.loading && search.allUsers.length === 0 ? (
            <TableSkeleton rows={4} cols={2} />
          ) : search.filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-400">조건에 맞는 직원이 없습니다</div>
          ) : (
            <div className={`${ADMIN_LIST_CAPPED} bg-white border border-slate-200 rounded-lg divide-y divide-slate-100`}>
              {search.filteredUsers.map(user => (
                <UserPickerRow
                  key={String(user.id)}
                  user={user}
                  onlineUsers={[]}
                  showPresence={false}
                  onToggle={search.toggleUser}
                  badge={
                    isEditor(user.id) ? (
                      <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-brand-100 text-brand-700">담당자</span>
                    ) : undefined
                  }
                />
              ))}
            </div>
          )}
        </div>
      )}

      {loading && !state ? (
        <TableSkeleton rows={3} cols={3} />
      ) : total === 0 ? (
        <div className="text-center py-8 border border-dashed border-slate-200 rounded-xl">
          <p className="text-sm text-slate-500">아직 회사 일정 담당자가 없습니다</p>
          <p className="mt-1 text-xs text-slate-400">담당자를 지정하면 그 사람의 일정 편집기에 '회사 일정'이 생깁니다</p>
        </div>
      ) : (
        <div className={`${ADMIN_LIST_FILL} space-y-2`}>
          {/* Super Admin은 명단 없이 자동 — 빼려면 역할 관리에서 Super Admin을 회수한다 */}
          {superAdminsOnly.map(sa => (
            <div
              key={`sa:${sa.userId}`}
              className="flex items-center justify-between gap-3 p-3 bg-white border border-slate-200 rounded-xl"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{sa.userName || sa.userId}</div>
                <div className="text-xs text-slate-400 truncate">
                  사번 {sa.userId} · Super Admin이라 모든 계열사의 회사 일정을 관리합니다
                </div>
              </div>
              <span className="shrink-0 px-2 py-0.5 text-xs rounded-full font-medium bg-slate-100 text-slate-600">자동</span>
            </div>
          ))}
          {(state?.editors ?? []).map(row => (
            <div
              key={`${row.affiliateId}:${row.userId}`}
              className="flex items-center justify-between gap-3 p-3 bg-white border border-slate-200 rounded-xl"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">
                  {row.userName || row.userId}
                  {row.depart && <span className="ml-2 text-xs font-normal text-slate-500">{row.depart}</span>}
                  {superAdminIds.has(row.userId) && (
                    <span className="ml-2 px-1.5 py-0.5 text-[11px] rounded-full font-medium bg-slate-100 text-slate-600">Super Admin</span>
                  )}
                </div>
                <div className="text-xs text-slate-400 truncate">
                  {affiliateLabelOf(row.affiliateId)} · 사번 {row.userId}
                  {row.grantedByName && <> · {row.grantedByName} 지정</>}
                  {' · '}
                  {new Date(row.createdAt).toLocaleDateString('ko-KR')}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setRevokeTarget(row)}
                disabled={busy}
                className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-brand-600 text-brand-600 rounded-lg hover:bg-brand-50 transition-colors disabled:opacity-50"
              >
                <UserMinus size={14} aria-hidden="true" />
                해제
              </button>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!revokeTarget}
        title="담당자 해제"
        message={
          revokeTarget
            ? superAdminIds.has(revokeTarget.userId)
              ? `${revokeTarget.userName || revokeTarget.userId}님을 명단에서 뺄까요? Super Admin이라 명단에서 빠져도 계속 회사 일정을 관리합니다.`
              : `${revokeTarget.userName || revokeTarget.userId}님을 회사 일정 담당자에서 해제할까요? 더는 회사 일정을 등록·수정할 수 없고, 이미 등록한 일정은 그대로 남습니다.`
            : ''
        }
        confirmLabel="해제"
        danger
        onConfirm={() => {
          if (revokeTarget) performRevoke(revokeTarget);
          setRevokeTarget(null);
        }}
        onCancel={() => setRevokeTarget(null)}
      />
    </div>
  );
};
